import { Link } from "react-router-dom";
import type { Article } from "@/mocks/gossip";
import { formatCount } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  variant?: "grid" | "list";
}

export default function ArticleCard({ article, variant = "grid" }: ArticleCardProps) {
  if (variant === "list") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-44 h-40 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title}相关配图`}
            title={`${article.title} - 看料爆料`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="px-2 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap">
              {article.channelName}
            </span>
            <span className="text-xs text-foreground-500">{article.date}</span>
          </div>
          <h3 className="font-heading font-semibold text-base text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 hidden sm:block">
            {article.summary}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-eye-line" />{formatCount(article.views)}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line" />{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors">
      <Link to={`/article/${article.id}`} className="block cursor-pointer">
        <div className="w-full h-48 overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title}相关配图`}
            title={`${article.title} - 看料爆料`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      </Link>
      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-xs bg-accent-100 text-accent-900 whitespace-nowrap">
            {article.channelName}
          </span>
          <span className="text-xs text-foreground-500">{article.date}</span>
        </div>
        <h3 className="font-heading font-semibold text-lg text-foreground-950 leading-snug">
          <Link to={`/article/${article.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed line-clamp-3">{article.summary}</p>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1"><i className="ri-eye-line" />{formatCount(article.views)}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line" />{article.readTime}</span>
          </span>
          <span className="flex items-center gap-1 text-primary-600 font-medium">
            阅读全文<i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </article>
  );
}